import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { RawTexture } from '@babylonjs/core/Materials/Textures/rawTexture';
import { Color4, Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { VertexBuffer } from '@babylonjs/core/Meshes/buffer';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Logger } from '@babylonjs/core/Misc/logger';
import { Observable } from '@babylonjs/core/Misc/observable';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Scene } from '@babylonjs/core/scene';
import { FloatArray, Nullable } from '@babylonjs/core/types';
import { MeshNode } from '@models/classes/meshes/mesh-node';
import { KbVector } from '@models/classes/primitives/kb-vector';
import { getCameraPositionWithOffset } from '@view/helpers/math-helper';
import { KbViewer } from '@view/kb-viewer';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { eMarqueeSelectMode } from '../interaction';
import { KbGizmo } from './gizmo';

export interface VertexChanges {
    mesh: AbstractMesh;
    delta: Vector3;
    vertexIndices: number[];
}

/**
 * Gizmo that enables moving vertices on a mesh.
 */
export class KbVertexSelectGizmo extends KbGizmo {
    /** Fires an event when any of it's sub gizmos are dragged */
    public onSelectionChange = new Observable<{ selectedIndices: number[] }>();
    public vertexTracker = new Subject<string>();
    public particleScale = 0.005;
    public get attachedMesh() {
        return this.__attachedMesh;
    }

    public set attachedMesh(mesh: Nullable<AbstractMesh>) {
        this.__attachedMesh = mesh;
        if (this.particleSystem) {
            this.particleSystem.dispose();
            this.particleSystem = undefined;
        }
        if (mesh) {
            this.drawVertices(mesh);
        }
    }

    private verticesUtilLayer: UtilityLayerRenderer;
    private particleSystem?: ParticleSystem;
    private currentMeshVertexPositions?: FloatArray;
    private redrawVertices = false;
    private selectionsEnabled: boolean;
    private disposeMarquee: Subject<void>;
    private saveAnimate: (() => void) | undefined;

    private __attachedMesh: Nullable<AbstractMesh> = null;
    private selectedVertices = new Map<number, null>();

    readonly vertexSelectedColor = new Color4(0.4, 1, 0, 0.5);
    readonly vertexUnSelectedColor = new Color4(0, 0.2, 1, 0.5);
    readonly vertexTransparentColor = new Color4(0, 0, 0, 0);

    constructor(private kbViewer: KbViewer, private scene: Scene) {
        super(kbViewer.utilityLayer);
        this.verticesUtilLayer = kbViewer.utilityLayer;
        this.attachedMesh = null;
    }

    attachTo(target: MeshNode) {
        const mesh = this.kbViewer.getRendererNode(target);
        if (!mesh) {
            throw Error('Could not find mesh in scene to attach gizmo');
        }
        this.attachedMesh = mesh as Mesh;

        // Clean up old marquee
        if (this.disposeMarquee) {
            this.disposeMarquee.next();
            this.disposeMarquee.complete();
        }

        this.disposeMarquee = new Subject<void>();
        let selectionMode: null | 'new' | 'add' | 'remove' = null;

        this.kbViewer.interaction
            .enableMarqueeSelect(target, eMarqueeSelectMode.Vertices)
            .pipe(takeUntil(this.disposeMarquee))
            .subscribe(event => {
                if (selectionMode || event.vertexList.length > 0) {
                    const vertices = event.vertexList;

                    // If user is making a new selection, set the selection mode based on modifier keys
                    if (selectionMode === null) {
                        if (event.ctrlKey) {
                            selectionMode = 'add';
                        } else if (event.altKey) {
                            selectionMode = 'remove';
                        } else {
                            selectionMode = 'new';
                        }
                    }

                    if (selectionMode === 'new') {
                        this.selectedVertices.clear();
                    }
                    if (selectionMode === 'remove') {
                        vertices.forEach(vertex => this.selectedVertices.delete(vertex));
                    } else {
                        vertices.forEach(vertex => this.selectedVertices.set(vertex, null));
                    }
                    this.redraw();
                    this.emitSelectionChange();
                }
            });

        this.kbViewer.interaction.onPointerUp.pipe(takeUntil(this.disposeMarquee)).subscribe(() => {
            selectionMode = null;
        });
    }

    updateParticleScale(scale: number) {
        this.particleScale = scale;
        this.redraw();
    }

    updateGizmoPosition() {
        this.kbViewer.forceAndWaitForNextRender().then(() => {
            if (this.__attachedMesh) {
                this.currentMeshVertexPositions = this.__attachedMesh
                    .getVerticesData(VertexBuffer.PositionKind)
                    ?.slice() as any;
                if (!this.currentMeshVertexPositions) {
                    return;
                }
                this.redraw();
            }
        });
    }

    dispose() {
        if (this.particleSystem) {
            this.particleSystem.dispose();
            this.particleSystem = undefined;
        }
        this.disposeMarquee.next();
        this.disposeMarquee.complete();
        this.kbViewer.interaction.disableMarqueeSelect();
    }

    setCustomMesh(mesh: Mesh) {
        Logger.Error('Custom meshes are not supported on this gizmo.');
    }

    selectVertices(vertexIndices: number[]) {
        this.selectedVertices.clear();
        vertexIndices.forEach(vertex => this.selectedVertices.set(vertex, null));
        this.redraw();
        this.emitSelectionChange();
    }

    enableSelectionChange() {
        this.selectionsEnabled = true;
        this.redraw();
    }

    disableSelectionChange() {
        this.selectionsEnabled = false;
        this.redraw();
        this.vertexTracker.complete();
    }

    getBarycenter(indices: number[]) {
        let x = 0,
            y = 0,
            z = 0;
        const currentMeshVertexPositions = this.currentMeshVertexPositions;

        if (currentMeshVertexPositions) {
            indices.forEach(idx => {
                x += currentMeshVertexPositions[idx * 3];
                y += currentMeshVertexPositions[idx * 3 + 1];
                z += currentMeshVertexPositions[idx * 3 + 2];
            });
        }

        return new KbVector(x / indices.length, y / indices.length, z / indices.length);
    }

    private redraw() {
        this.vertexTracker.next(this.selectedVertices.size.toString());
        if (this.particleSystem && this.saveAnimate) {
            this.particleSystem.animate = this.saveAnimate;
            this.redrawVertices = true;
        }
    }

    private emitSelectionChange() {
        this.onSelectionChange.notifyObservers({ selectedIndices: Array.from(this.selectedVertices.keys()) });
    }

    private drawVertices(targetMesh: AbstractMesh) {
        if (!this.currentMeshVertexPositions) {
            this.currentMeshVertexPositions = targetMesh.getVerticesData(VertexBuffer.PositionKind)?.slice();
        }
        if (!this.currentMeshVertexPositions) {
            return;
        }

        const vertexCount = this.currentMeshVertexPositions.length / 3;
        this.particleSystem = new ParticleSystem(
            'vertexSelectParticleSystem',
            vertexCount,
            this.verticesUtilLayer.utilityLayerScene
        );

        const camera = this.scene.activeCamera instanceof ArcRotateCamera ? this.scene.activeCamera : null;
        const cameraDistance = Vector3.Zero();
        const cameraPosition = Vector3.Zero();
        this.saveAnimate = this.particleSystem.animate.bind(this.particleSystem);

        let particleIdx = 0;
        this.particleSystem.startPositionFunction = (worldMatrix, positionToUpdate, particle) => {
            particleIdx += 3;
            const vertexPositions = this.currentMeshVertexPositions!;
            Vector3.TransformCoordinatesFromFloatsToRef(
                vertexPositions[particleIdx],
                vertexPositions[particleIdx + 1],
                vertexPositions[particleIdx + 2],
                worldMatrix,
                positionToUpdate
            );
        };
        const transform = targetMesh.computeWorldMatrix(true);

        this.particleSystem.updateFunction = particles => {
            let particlesToProcess = false;
            if (camera) {
                cameraPosition.copyFrom(getCameraPositionWithOffset(camera));
            }

            for (let i = 0; i < particles.length; i++) {
                const particle = particles[i];
                const vertexPositions = this.currentMeshVertexPositions;

                if (vertexPositions && (particle.age === 0 || this.redrawVertices)) {
                    Vector3.TransformCoordinatesFromFloatsToRef(
                        vertexPositions[i * 3],
                        vertexPositions[i * 3 + 1],
                        vertexPositions[i * 3 + 2],
                        transform,
                        particle.position
                    );
                    particle.age = 1;
                    if (this.selectedVertices.has(i)) {
                        particle.color = this.vertexSelectedColor;
                    } else {
                        if (this.selectionsEnabled) {
                            particle.color = this.vertexUnSelectedColor;
                        } else {
                            particle.color = this.vertexTransparentColor;
                        }
                    }
                    if (!particlesToProcess) {
                        particlesToProcess = true;
                    }
                }

                if (camera) {
                    cameraPosition.subtractToRef(particle.position, cameraDistance);
                    particle.size = (this.particleScale - 0.001) * cameraDistance.length();
                } else {
                    particle.size = this.particleScale - 0.004;
                }
            }

            this.redrawVertices = false;
            // If there are a large number of particles, kill the updating function for performance
            if (particles.length && particles.length > 10000 && !particlesToProcess && this.particleSystem) {
                // stable
                this.particleSystem.stop();
                this.particleSystem.animate = () => void 0;
            }
        };

        const colorArray = new Uint8Array(3);
        for (let i = 0; i < colorArray.length; i += 3) {
            colorArray[i] = 225;
            colorArray[i + 1] = 225;
            colorArray[i + 2] = 225;
        }

        this.particleSystem.emitter = targetMesh;
        this.particleSystem.minSize = this.particleScale;
        this.particleSystem.maxSize = this.particleScale;
        this.particleSystem.disposeOnStop = false;
        this.particleSystem.particleTexture = RawTexture.CreateRGBTexture(
            colorArray,
            1,
            1,
            this.verticesUtilLayer.utilityLayerScene
        );
        this.particleSystem.manualEmitCount = vertexCount;
        // this.particleSystem.emitRate
        this.particleSystem.start();
    }
}
